import { FormEvent, useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, InputAdornment, MenuItem, Paper,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography,
} from "@mui/material";
import { AlertTriangle, ExternalLink, FileSpreadsheet, FileText, FolderOpen, Globe, Pencil, Plus, Search, Trash2 } from "lucide-react";
import {
  createResource, deleteResource, getResourceCategories, getResources,
  recordResourceView, updateResource,
} from "./resourcesApi";
import { ResourceCategory, ResourceItem } from "./types";
import { getResourcePresentation } from "./resourcePresentation";

const brand = "#805746";
const initialForm = {
  title: "", description: "", category: "",
  resource_type: "website" as ResourceItem["resource_type"], url: "", tags: [] as string[],
};
const typeIcons = { google_docs: FileText, google_drive: FolderOpen, google_sheets: FileSpreadsheet, pdf: FileText, website: Globe };
const detectType = (url: string): ResourceItem["resource_type"] => {
  const value = url.toLowerCase();
  if (value.includes("docs.google.com/spreadsheets") || value.includes("sheets.google.com")) return "google_sheets";
  if (value.includes("docs.google.com/document")) return "google_docs";
  if (value.includes("drive.google.com")) return "google_drive";
  if (value.endsWith(".pdf") || value.includes(".pdf?")) return "pdf";
  return "website";
};
const slugify = (value: string) => value.toLowerCase().trim()
  .replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export default function ResourcesPage() {
  const [resources, setResources] = useState<ResourceItem[]>([]);
  const [categories, setCategories] = useState<ResourceCategory[]>([]);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [tagInput, setTagInput] = useState("");
  const [editing, setEditing] = useState<ResourceItem | null>(null);
  const [detail, setDetail] = useState<ResourceItem | null>(null);
  const [deleting, setDeleting] = useState<ResourceItem | null>(null);

  useEffect(() => {
    let active = true;
    Promise.all([getResources(), getResourceCategories()])
      .then(([items, categoryItems]) => {
        if (active) { setResources(items); setCategories(categoryItems); }
      })
      .catch(() => active && setError("Docs & Links could not be loaded."))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return resources.filter((resource) =>
      (category === "all" || resource.category_slug === category) &&
      (!needle || `${resource.title} ${resource.description} ${resource.category_name} ${resource.tags.join(" ")}`
        .toLowerCase().includes(needle)));
  }, [resources, query, category]);

  const openResource = (resource: ResourceItem) => {
    void recordResourceView(resource.id).catch(() => undefined);
    if (resource.destination_url) window.open(resource.destination_url, "_blank", "noopener,noreferrer");
  };

  const openAdd = () => {
    setEditing(null);
    setForm(initialForm);
    setTagInput("");
    setDialogOpen(true);
  };

  const openEdit = (resource: ResourceItem) => {
    setDetail(null);
    setEditing(resource);
    setForm({
      title: resource.title,
      description: resource.description,
      category: String(resource.category),
      resource_type: resource.resource_type,
      url: resource.destination_url,
      tags: resource.tags || [],
    });
    setTagInput("");
    setDialogOpen(true);
  };

  const submitResource = async (event: FormEvent) => {
    event.preventDefault();
    if (!form.title || !form.category || !form.url) return;
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        slug: editing?.slug || `${slugify(form.title)}-${Date.now().toString().slice(-6)}`,
        description: form.description.trim(),
        tags: form.tags,
        category: Number(form.category),
        resource_type: detectType(form.url),
        url: form.url.trim(),
        is_featured: false,
        is_published: true,
      };
      const item = editing
        ? await updateResource(editing.id, payload)
        : await createResource(payload);
      setResources((current) => editing
        ? current.map((resource) => resource.id === item.id ? item : resource)
        : [item, ...current]);
      setForm(initialForm);
      setEditing(null);
      setDialogOpen(false);
    } catch {
      setError("The document or link could not be saved. Check the details and try again.");
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setSaving(true);
    try {
      await deleteResource(deleting.id);
      setResources((current) => current.filter((resource) => resource.id !== deleting.id));
      setDeleting(null);
      setDetail(null);
    } catch {
      setError("The document or link could not be deleted.");
    } finally { setSaving(false); }
  };

  return (
    <Box sx={{ minHeight: "calc(100vh - 170px)", px: { xs: 0, md: 1 }, pb: 4 }}>
      <Stack direction={{ xs: "column", sm: "row" }} alignItems={{ xs: "stretch", sm: "flex-end" }} justifyContent="space-between" gap={2} mb={4}>
        <Box>
          <Typography color={brand} fontSize={14} mb={0.6}>Muzukuru library</Typography>
          <Typography component="h1" fontSize={{ xs: 36, md: 48 }} lineHeight={1.05} fontWeight={800} letterSpacing="-.04em">Docs &amp; Links</Typography>
        </Box>
        <Button variant="contained" startIcon={<Plus size={19} />} onClick={openAdd} sx={{ bgcolor: brand, px: 3.5, py: 1.5, borderRadius: 3, textTransform: "none", fontWeight: 750, boxShadow: "0 9px 20px rgba(78,50,39,.2)", "&:hover": { bgcolor: "#684536" } }}>Add Document or Link</Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2.5, borderRadius: 3 }}>{error}</Alert>}
      <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 4, border: "1px solid", borderColor: "divider" }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" gap={2} mb={3}>
          <Box><Typography fontWeight={800}>All docs &amp; links</Typography><Typography color="text.secondary" fontSize={12.5}>Browse the team knowledge library</Typography></Box>
          <Stack direction={{ xs: "column", sm: "row" }} gap={1.5}>
            <TextField select size="small" value={category} onChange={(event) => setCategory(event.target.value)} sx={{ minWidth: 160 }} aria-label="Filter Docs and Links category">
              <MenuItem value="all">All categories</MenuItem>
              {categories.map((item) => <MenuItem key={item.id} value={item.slug}>{item.name}</MenuItem>)}
            </TextField>
            <TextField size="small" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search docs & links" inputProps={{ "aria-label": "Search docs and links" }} InputProps={{ startAdornment: <InputAdornment position="start"><Search size={17} /></InputAdornment> }} sx={{ minWidth: { sm: 265 } }} />
          </Stack>
        </Stack>

        <TableContainer>
          <Table sx={{ minWidth: 720 }}>
            <TableHead><TableRow sx={{ "& th": { bgcolor: brand, color: "white", border: 0, fontWeight: 750, py: 2.2 }, "& th:first-of-type": { borderRadius: "18px 0 0 18px" }, "& th:last-of-type": { borderRadius: "0 18px 18px 0" } }}>
              <TableCell>Document or link</TableCell><TableCell>Category</TableCell><TableCell>Type</TableCell><TableCell>Last Updated</TableCell><TableCell>Action</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={5} align="center" sx={{ py: 8 }}><CircularProgress size={30} sx={{ color: brand }} /></TableCell></TableRow>
                : filtered.length === 0 ? <TableRow><TableCell colSpan={5} align="center" sx={{ py: 8 }}><Typography fontWeight={700}>No docs or links found</Typography><Typography color="text.secondary" fontSize={13}>Add the first document or link, or adjust your filters.</Typography></TableCell></TableRow>
                : filtered.map((resource) => {
                  const Icon = typeIcons[resource.resource_type] ?? FileText;
                  const presentation = getResourcePresentation(resource);
                  return <TableRow key={resource.id} hover sx={{ "& td": { py: 2.6, borderColor: "divider" }, cursor: "pointer" }} onClick={() => setDetail(resource)}>
                    <TableCell><Stack direction="row" alignItems="center" spacing={2}><Box sx={{ width: 40, height: 40, borderRadius: 2.5, display: "grid", placeItems: "center", bgcolor: `${resource.category_color || "#4278F5"}14`, color: resource.category_color || "#4278F5", flexShrink: 0 }}><Icon size={20} /></Box><Box><Typography fontWeight={800}>{resource.title}</Typography><Typography color="text.secondary" fontSize={13} sx={{ maxWidth: 360 }} noWrap>{resource.description || resource.destination_url}</Typography></Box></Stack></TableCell>
                    <TableCell>{resource.category_name}</TableCell>
                    <TableCell><Chip size="small" label={presentation.label} sx={{ fontWeight: 650 }} /></TableCell>
                    <TableCell><Typography fontSize={13.5}>{new Intl.DateTimeFormat("en-ZA", { day: "numeric", month: "long", year: "numeric" }).format(new Date(resource.updated_at))}</Typography>{resource.created_by_name && <Typography color="text.secondary" fontSize={12}>by {resource.created_by_name}</Typography>}</TableCell>
                    <TableCell><Stack direction="row" alignItems="center" spacing={1}>
                      <IconButton size="small" aria-label={`Edit ${resource.title}`} onClick={(event) => { event.stopPropagation(); openEdit(resource); }} sx={{ color: brand }}><Pencil size={18} /></IconButton>
                      <IconButton size="small" aria-label={`Delete ${resource.title}`} onClick={(event) => { event.stopPropagation(); setDeleting(resource); }} sx={{ color: "#e85d64" }}><Trash2 size={19} /></IconButton>
                      <Button size="small" variant="outlined" onClick={(event) => { event.stopPropagation(); openResource(resource); }} sx={{ borderColor: "#c2aaa2", color: brand, borderRadius: 99, textTransform: "none", whiteSpace: "nowrap" }}>Open</Button>
                    </Stack></TableCell>
                  </TableRow>;
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 4 } }}>
        <Box component="form" onSubmit={submitResource}>
          <DialogTitle fontWeight={800} fontSize={24}>{editing ? "Edit Document or Link" : "Add Document or Link"}</DialogTitle>
          <DialogContent><Stack spacing={2.2} pt={1}>
            {categories.length === 0 && <Alert severity="info">Create at least one category before adding documents or links.</Alert>}
            <TextField required label="Name" value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} />
            <TextField label="Description" multiline minRows={3} value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} />
            <TextField required select label="Category" value={form.category} onChange={(event) => setForm({ ...form, category: event.target.value })}>{categories.map((item) => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}</TextField>
            <TextField required type="url" label="Link URL" placeholder="https://..." value={form.url} onChange={(event) => setForm({ ...form, url: event.target.value })} />
            <TextField label="Tags" placeholder="Type a tag and press Enter" value={tagInput} onChange={(event) => setTagInput(event.target.value)} onKeyDown={(event) => {
              if ((event.key === "Enter" || event.key === ",") && tagInput.trim()) {
                event.preventDefault();
                const tag = tagInput.trim().toLowerCase().replace(/,/g, "");
                if (!form.tags.includes(tag)) setForm({ ...form, tags: [...form.tags, tag] });
                setTagInput("");
              }
            }} helperText={form.tags.length ? form.tags.join(" · ") : "Optional"} />
          </Stack></DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}><Button onClick={() => setDialogOpen(false)} disabled={saving} sx={{ color: "text.secondary", textTransform: "none" }}>Cancel</Button><Button type="submit" variant="contained" disabled={saving || categories.length === 0} sx={{ bgcolor: brand, textTransform: "none", px: 3, "&:hover": { bgcolor: "#684536" } }}>{saving ? "Saving..." : editing ? "Save Changes" : "Add Document or Link"}</Button></DialogActions>
        </Box>
      </Dialog>

      <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 4 } }}>
        {detail && <><DialogTitle fontWeight={800}>{detail.title}</DialogTitle><DialogContent>
          <Typography color={brand} fontWeight={700} fontSize={13}>{detail.category_name}</Typography>
          <Typography color="text.secondary" lineHeight={1.7} mt={2}>{detail.description}</Typography>
          <Paper variant="outlined" sx={{ p: 2, mt: 3, borderRadius: 3, bgcolor: "action.hover" }}><Typography fontWeight={700}>{detail.resource_type_display}</Typography><Typography color="text.secondary" fontSize={12.5} noWrap>{detail.destination_url}</Typography></Paper>
          {detail.tags?.length > 0 && <Stack direction="row" gap={1} flexWrap="wrap" mt={3}>{detail.tags.map((tag) => <Chip key={tag} label={tag} size="small" />)}</Stack>}
        </DialogContent><DialogActions sx={{ px: 3, pb: 3 }}>
          <IconButton onClick={() => setDeleting(detail)} sx={{ mr: "auto", color: "#e85d64" }}><Trash2 size={18} /></IconButton>
          <Button startIcon={<Pencil size={16} />} onClick={() => openEdit(detail)} sx={{ color: brand, textTransform: "none" }}>Edit</Button>
          <Button variant="contained" startIcon={<ExternalLink size={16} />} onClick={() => openResource(detail)} sx={{ bgcolor: brand, textTransform: "none", "&:hover": { bgcolor: "#684536" } }}>Open</Button>
        </DialogActions></>}
      </Dialog>

      <Dialog open={Boolean(deleting)} onClose={() => !saving && setDeleting(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogContent sx={{ textAlign: "center", pt: 4 }}><Box sx={{ width: 48, height: 48, borderRadius: "50%", bgcolor: "#fff0f0", color: "#e85d64", display: "grid", placeItems: "center", mx: "auto" }}><AlertTriangle size={22} /></Box><Typography fontWeight={800} fontSize={20} mt={2}>Delete this document or link?</Typography><Typography color="text.secondary" mt={1}>“{deleting?.title}” will be removed from MMS, but the original document will not be deleted.</Typography></DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}><Button onClick={() => setDeleting(null)} disabled={saving} sx={{ textTransform: "none" }}>Cancel</Button><Button variant="contained" color="error" onClick={() => void confirmDelete()} disabled={saving} sx={{ textTransform: "none" }}>Delete</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
